import { Injectable } from '@angular/core';
import { Observable, catchError, of, shareReplay } from 'rxjs';

import { Logger } from '@sondermind/datadog-logging';
import { IAPIListRequestOptions, IAPIListResponse, IBrowseLinkApiResponse } from '@sondermind/utilities/models-http';

import { BrowseStateHttpService } from '../services/browse-state.httpservice';

const EMPTY_LIST_RESPONSE: IAPIListResponse<IBrowseLinkApiResponse> = {
  data: [],
  meta: { view: '', links: { self: '' }, counts: { total: 0 } }
};

@Injectable({
  providedIn: 'root'
})
export class BrowseLinksStore {
  private browseLinksObservables: Map<string, Observable<IAPIListResponse<IBrowseLinkApiResponse>>> = new Map();
  private browseCityLinksObservables: Map<string, Observable<IAPIListResponse<IBrowseLinkApiResponse>>> = new Map();

  constructor(
    private browseStateHttpService: BrowseStateHttpService,
    private logger: Logger
  ) {}

  /**
   * Get browse links for a specific state, using cache if available
   * @param state - The state abbreviation
   * @param forceRefresh - Whether to force a refresh from the API
   * @returns Observable of browse links data
   */
  getBrowseLinks$(state: string, forceRefresh: boolean = false): Observable<IAPIListResponse<IBrowseLinkApiResponse>> {
    // If forcing refresh, clear the cached observable
    if (forceRefresh) {
      this.browseLinksObservables.delete(state);
    }

    // Return cached observable if available
    if (this.browseLinksObservables.has(state)) {
      return this.browseLinksObservables.get(state) as Observable<IAPIListResponse<IBrowseLinkApiResponse>>;
    }

    // Create new observable and cache it
    const stateOptions: IAPIListRequestOptions = {
      page: 'disabled' as const,
      filter: { key: 'state', value: state },
    };

    const observable = this.browseStateHttpService.getBrowseLinks(stateOptions).pipe(
      catchError((err) => {
        this.logger.warn('BrowseLinksStore: getBrowseLinks failed, returning empty list', { err, state });
        this.browseLinksObservables.delete(state);
        return of(EMPTY_LIST_RESPONSE);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.browseLinksObservables.set(state, observable);
    return observable;
  }

  /**
   * Get browse city links for a specific state and city, using cache if available
   * @param state - The state abbreviation
   * @param city - The city name
   * @param forceRefresh - Whether to force a refresh from the API
   * @returns Observable of browse city links data
   */
  getBrowseCityLinks$(
    state: string,
    city: string,
    forceRefresh: boolean = false
  ): Observable<IAPIListResponse<IBrowseLinkApiResponse>> {
    const cacheKey = `${state}-${city}`;

    // If forcing refresh, clear the cached observable
    if (forceRefresh) {
      this.browseCityLinksObservables.delete(cacheKey);
    }

    // Return cached observable if available
    if (this.browseCityLinksObservables.has(cacheKey)) {
      return this.browseCityLinksObservables.get(cacheKey) as Observable<IAPIListResponse<IBrowseLinkApiResponse>>;
    }

    // Create new observable and cache it
    const cityOptions: IAPIListRequestOptions = {
      page: 'disabled' as const,
      filter: [
        { key: 'state', value: state },
        { key: 'city', value: city }
      ],
    };

    const observable = this.browseStateHttpService.getBrowseCityLinks(cityOptions).pipe(
      catchError((err) => {
        this.logger.warn('BrowseLinksStore: getBrowseCityLinks failed, returning empty list', { err, state, city });
        this.browseCityLinksObservables.delete(cacheKey);
        return of(EMPTY_LIST_RESPONSE);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.browseCityLinksObservables.set(cacheKey, observable);
    return observable;
  }

  /**
   * Clear the cache for a specific state or all states
   * @param state - Optional state to clear cache for. If not provided, clears all cache.
   */
  clearCache(state?: string): void {
    if (state) {
      this.browseLinksObservables.delete(state);
      // Clear city links cache for this state
      const keysToDelete = Array.from(this.browseCityLinksObservables.keys())
        .filter((key) => key.startsWith(`${state}-`));
      keysToDelete.forEach((key) => this.browseCityLinksObservables.delete(key));
    } else {
      this.browseLinksObservables.clear();
      this.browseCityLinksObservables.clear();
    }
  }
}
